import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Shield, LayoutGrid, Image as ImageIcon, Megaphone, BarChart3, Mail,
  Plus, Pencil, Trash2, Eye, Star, Loader2, MapPin, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, resolveUrl, formatApiErrorDetail } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { ContactFields } from "@/components/admin/ContactFields";
import { LocationPicker } from "@/components/admin/LocationPicker";
import { BusinessExtraFields } from "@/components/admin/BusinessExtraFields";
import { EventsManager } from "@/components/admin/EventsManager";
import { SUBCATEGORIES, SPORTS } from "@/constants/categories";

import { ListingsView } from "@/components/admin/ListingsView";
import { MessagesTab } from "@/components/admin/MessagesTab";
import { BusinessStats } from "@/components/admin/BusinessStats";

const SPORT_SUBCATS = ["athlitikoi", "amea"];

const TABS = [
  { id: "listings", label: "Δραστηριότητες", icon: LayoutGrid },
  { id: "banners", label: "Banners", icon: ImageIcon },
  { id: "promotions", label: "Προσφορές", icon: Megaphone },
  { id: "messages", label: "Μηνύματα", icon: Mail },
  { id: "analytics", label: "Στατιστικά", icon: BarChart3 },
];

const EMPTY_BIZ = { name: "", category: "", subcategory: "", sport: "", audiences: [], age_groups: [], tags: [], city: "", address: "", postal_code: "", lat: "", lng: "", locations: [], logo: "", cover_image: "", video_url: "", description: "", about: "", weekly_schedule_text: "", weekly_schedule_photos: [], photos: [], highlights: [], departments: [], opening_hours: [], facilities: [], amenities: [], social: {}, contacts: [], verified: false, active: true, featured: false };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("listings");
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [banners, setBanners] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [messages, setMessages] = useState([]);
  const unread = messages.filter((m) => !m.read).length;

  const [bizModal, setBizModal] = useState(null);
  const [bannerModal, setBannerModal] = useState(null);
  const [promoModal, setPromoModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/businesses", { params: { include_inactive: true, sort: "name" } }).then((r) => setBusinesses(r.data));
    api.get("/banners-all").then((r) => setBanners(r.data)).catch(() => {});
    api.get("/promotions").then((r) => setPromotions(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setMessages(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadAll();
    const poll = setInterval(() => api.get("/admin/contacts").then((r) => setMessages(r.data)).catch(() => {}), 30000);
    return () => clearInterval(poll);
    // eslint-disable-next-line
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // ---- Business CRUD ----
  const saveBiz = async () => {
    setSaving(true);
    try {
      const locations = (bizModal.locations || []).filter((l) => l && (l.address || l.lat != null));
      const primary = locations[0] || {};
      const payload = {
        ...bizModal,
        category: (bizModal.audiences || [])[0] || "",
        locations,
        address: primary.address || "",
        lat: primary.lat ?? null,
        lng: primary.lng ?? null,
        contacts: (bizModal.contacts || []).filter((c) => c.value && c.value.trim()),
      };
      if (bizModal.id) await api.put(`/businesses/${bizModal.id}`, payload);
      else await api.post("/businesses", payload);
      toast.success("Αποθηκεύτηκε");
      setBizModal(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const delBiz = async (id) => {
    if (!window.confirm("Διαγραφή δραστηριότητας;")) return;
    await api.delete(`/businesses/${id}`);
    toast.success("Διαγράφηκε");
    loadAll();
  };

  // ---- Banner CRUD ----
  const saveBanner = async () => {
    setSaving(true);
    try {
      if (!bannerModal.image) { toast.error("Προσθέστε εικόνα"); setSaving(false); return; }
      if (bannerModal.id) await api.put(`/banners/${bannerModal.id}`, bannerModal);
      else await api.post("/banners", bannerModal);
      toast.success("Αποθηκεύτηκε");
      setBannerModal(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };
  const delBanner = async (id) => { if (!window.confirm("Διαγραφή banner;")) return; await api.delete(`/banners/${id}`); loadAll(); };

  // ---- Promotion CRUD ----
  const savePromo = async () => {
    setSaving(true);
    try {
      if (promoModal.id) await api.put(`/promotions/${promoModal.id}`, promoModal);
      else await api.post("/promotions", promoModal);
      toast.success("Αποθηκεύτηκε");
      setPromoModal(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };
  const delPromo = async (id) => { if (!window.confirm("Διαγραφή προσφοράς;")) return; await api.delete(`/promotions/${id}`); loadAll(); };


  return (
    <div className="min-h-screen bg-muted/40 flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 bg-slate-900 text-white lg:min-h-screen p-4 lg:p-6" data-testid="admin-sidebar">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center"><Shield size={18} /></div>
          <div>
            <p className="font-display font-extrabold leading-none">SkillCity</p>
            <p className="text-xs text-slate-400 mt-0.5">{user?.role === "admin" ? "Διαχειριστής" : "Αναγνώστης"}</p>
          </div>
        </div>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                tab === t.id ? "bg-primary text-white" : "text-slate-300 hover:bg-slate-800"
              }`}
            >
              <t.icon size={18} /> {t.label}
              {t.id === "messages" && unread > 0 && (
                <span data-testid="admin-messages-badge" className="ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-red-500 text-white text-[11px] font-bold animate-pulse">{unread}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="mt-8 pt-6 border-t border-slate-700 space-y-1">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-300 hover:bg-slate-800 w-full">
            <ExternalLink size={18} /> Ιστότοπος
          </button>
          <button onClick={handleLogout} data-testid="admin-logout-button" className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-300 hover:bg-slate-800 w-full">
            <LogOut size={18} /> Αποσύνδεση
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-4 sm:p-6 lg:p-10 max-w-6xl">
        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
            {[
              { label: "Δραστηριότητες", value: stats.total_businesses, icon: LayoutGrid },
              { label: "Banners", value: stats.total_banners, icon: ImageIcon },
              { label: "Αξιολογήσεις", value: stats.total_reviews, icon: Star },
              { label: "Προβολές", value: stats.total_views, icon: Eye },
            ].map((s) => (
              <div key={s.label} className="bg-white dark:bg-slate-800 rounded-2xl border border-border p-4 sm:p-5" data-testid="admin-stats-card">
                <s.icon className="text-primary mb-2" size={20} />
                <p className="text-2xl font-extrabold font-display">{s.value}</p>
                <p className="text-xs text-muted-foreground font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Listings */}
        {tab === "listings" && (
          <section>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-bold">Δραστηριότητες</h2>
              {isAdmin && (
                <Button className="rounded-full gap-1.5" onClick={() => setBizModal({ ...EMPTY_BIZ })} data-testid="admin-create-listing-button">
                  <Plus size={16} /> Νέα
                </Button>
              )}
            </div>
            <ListingsView businesses={businesses} isAdmin={isAdmin} onDelete={delBiz}
              onEdit={(b) => api.get(`/businesses/${b.id}`, { params: { raw: 1 } }).then(({ data: b }) => setBizModal({ ...EMPTY_BIZ, ...b, lat: b.lat ?? "", lng: b.lng ?? "", locations: (b.locations && b.locations.length) ? b.locations : (b.lat != null && b.lng != null ? [{ address: b.address || "", lat: b.lat, lng: b.lng }] : []) }))} />
          </section>
        )}

        {/* Banners */}
        {tab === "banners" && (
          <section>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-bold">Banners</h2>
              {isAdmin && <Button className="rounded-full gap-1.5" onClick={() => setBannerModal({ title: "", image: "", link: "", active: true, order: banners.length })} data-testid="admin-create-banner-button"><Plus size={16} /> Νέο</Button>}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {banners.map((b) => (
                <div key={b.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-border overflow-hidden" data-testid="admin-banner-card">
                  <img src={resolveUrl(b.image)} alt={b.title} className="w-full h-32 object-cover" />
                  <div className="p-3 flex items-center justify-between">
                    <span className="font-semibold text-sm truncate">{b.title || "—"}</span>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <button onClick={() => setBannerModal(b)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-muted"><Pencil size={14} /></button>
                        <button onClick={() => delBanner(b.id)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-muted text-destructive"><Trash2 size={14} /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Promotions */}
        {tab === "promotions" && (
          <section>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-bold">Προσφορές</h2>
              {isAdmin && <Button className="rounded-full gap-1.5" onClick={() => setPromoModal({ title: "", description: "", image: "", active: true })} data-testid="admin-create-promo-button"><Plus size={16} /> Νέα</Button>}
            </div>
            <div className="space-y-3">
              {promotions.map((p) => (
                <div key={p.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-border p-4 flex items-center gap-4" data-testid="admin-promo-row">
                  {p.image && <img src={resolveUrl(p.image)} alt="" className="h-16 w-16 rounded-xl object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{p.title}</p>
                    <p className="text-sm text-muted-foreground truncate">{p.description}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${p.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}>{p.active ? "Ενεργή" : "Ανενεργή"}</span>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <button onClick={() => setPromoModal(p)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-muted"><Pencil size={14} /></button>
                      <button onClick={() => delPromo(p.id)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-muted text-destructive"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Messages */}
        {tab === "messages" && <MessagesTab messages={messages} setMessages={setMessages} />}

        {/* Analytics */}
        {tab === "analytics" && <BusinessStats businesses={businesses} />}
      </main>

      {/* Business modal */}
      <Dialog open={!!bizModal} onOpenChange={(o) => !o && setBizModal(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="business-modal">
          <DialogHeader><DialogTitle className="font-display">{bizModal?.id ? "Επεξεργασία" : "Νέα δραστηριότητα"}</DialogTitle></DialogHeader>
          {bizModal && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Όνομα" testid="biz-name-input" value={bizModal.name} onChange={(v) => setBizModal({ ...bizModal, name: v })} />
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Υποκατηγορία</label>
                  <select
                    value={bizModal.subcategory || ""}
                    onChange={(e) => setBizModal({ ...bizModal, subcategory: e.target.value, sport: SPORT_SUBCATS.includes(e.target.value) ? bizModal.sport : "" })}
                    data-testid="biz-subcategory-select"
                    className="w-full h-10 px-3 rounded-xl bg-muted outline-none focus:ring-2 ring-primary"
                  >
                    <option value="">— Επιλέξτε —</option>
                    {Object.values(SUBCATEGORIES).map((s) => (
                      <option key={s.slug} value={s.slug}>{s.title}</option>
                    ))}
                  </select>
                </div>
                {SPORT_SUBCATS.includes(bizModal.subcategory) && (
                  <div>
                    <label className="block text-sm font-semibold mb-1.5">Άθλημα</label>
                    <select
                      value={bizModal.sport || ""}
                      onChange={(e) => setBizModal({ ...bizModal, sport: e.target.value })}
                      data-testid="biz-sport-select"
                      className="w-full h-10 px-3 rounded-xl bg-muted outline-none focus:ring-2 ring-primary"
                    >
                      <option value="">— Επιλέξτε άθλημα —</option>
                      {Object.values(SPORTS).map((s) => (
                        <option key={s.slug} value={s.slug}>{s.title}</option>
                      ))}
                    </select>
                  </div>
                )}
                <Field label="Πόλη" value={bizModal.city} onChange={(v) => setBizModal({ ...bizModal, city: v })} />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Κοινό</label>
                <div className="flex gap-4">
                  {[["paidia", "Παιδιά"], ["enilikes", "Ενήλικες"]].map(([val, lbl]) => {
                    const checked = (bizModal.audiences || []).includes(val);
                    return (
                      <label key={val} className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          data-testid={`biz-audience-${val}`}
                          onChange={(e) => {
                            const cur = new Set(bizModal.audiences || []);
                            e.target.checked ? cur.add(val) : cur.delete(val);
                            setBizModal({ ...bizModal, audiences: Array.from(cur) });
                          }}
                          className="h-4 w-4 accent-primary"
                        />
                        {lbl}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Ηλικιακές ομάδες</label>
                <div className="flex gap-4">
                  {["0-5", "6-12", "13-17"].map((val) => (
                    <label key={val} className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(bizModal.age_groups || []).includes(val)}
                        data-testid={`biz-age-${val}`}
                        onChange={(e) => {
                          const cur = new Set(bizModal.age_groups || []);
                          e.target.checked ? cur.add(val) : cur.delete(val);
                          setBizModal({ ...bizModal, age_groups: Array.from(cur) });
                        }}
                        className="h-4 w-4 accent-primary"
                      />
                      {val} ετών
                    </label>
                  ))}
                </div>
              </div>
              <LocationPicker value={bizModal.locations || []} onChange={(v) => setBizModal({ ...bizModal, locations: v })} />
              <div>
                <label className="block text-sm font-semibold mb-1.5">Περιγραφή</label>
                <textarea rows={3} value={bizModal.description} onChange={(e) => setBizModal({ ...bizModal, description: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-muted outline-none resize-none" data-testid="biz-description-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Εβδομαδιαίο πρόγραμμα — κείμενο</label>
                <textarea rows={3} value={bizModal.weekly_schedule_text} onChange={(e) => setBizModal({ ...bizModal, weekly_schedule_text: e.target.value })} data-testid="biz-weekly-text-input" placeholder="Γράψε το πρόγραμμα ή ανέβασε φωτογραφίες παρακάτω" className="w-full px-4 py-3 rounded-xl bg-muted outline-none resize-none" />
                <p className="text-xs text-muted-foreground mt-1">Αν ανεβάσεις φωτογραφίες προγράμματος, θα εμφανίζονται αυτές· αλλιώς εμφανίζεται το κείμενο.</p>
              </div>
              <ImageUpload label="Φωτογραφίες προγράμματος (προαιρετικά)" multiple max={6} value={bizModal.weekly_schedule_photos} onChange={(v) => setBizModal({ ...bizModal, weekly_schedule_photos: v })} />
              <ImageUpload label="Λογότυπο επιχείρησης (στρογγυλό)" value={bizModal.logo} onChange={(v) => setBizModal({ ...bizModal, logo: v })} />
              <ImageUpload label="Φωτογραφίες" multiple max={6} value={bizModal.photos} onChange={(v) => setBizModal({ ...bizModal, photos: v })} />
              <ContactFields contacts={bizModal.contacts || []} onChange={(v) => setBizModal((m) => ({ ...m, contacts: v }))} />

              <BusinessExtraFields biz={bizModal} set={(patch) => setBizModal((m) => ({ ...m, ...patch }))} />

              <div className="rounded-xl border border-border p-3">
                <h4 className="font-bold text-sm text-slate-800 mb-2">Νέα / Εκδηλώσεις</h4>
                <EventsManager businessId={bizModal.id} />
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={bizModal.active} onCheckedChange={(v) => setBizModal({ ...bizModal, active: v })} data-testid="biz-active-switch" /> Ενεργή</label>
                <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={bizModal.featured} onCheckedChange={(v) => setBizModal({ ...bizModal, featured: v })} /> Προβεβλημένη</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setBizModal(null)}>Άκυρο</Button>
            <Button className="rounded-full" onClick={saveBiz} disabled={saving} data-testid="biz-save-button">{saving ? <Loader2 className="animate-spin" size={16} /> : "Αποθήκευση"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Banner modal */}
      <Dialog open={!!bannerModal} onOpenChange={(o) => !o && setBannerModal(null)}>
        <DialogContent data-testid="banner-modal">
          <DialogHeader><DialogTitle className="font-display">{bannerModal?.id ? "Επεξεργασία banner" : "Νέο banner"}</DialogTitle></DialogHeader>
          {bannerModal && (
            <div className="space-y-4">
              <Field label="Τίτλος" value={bannerModal.title} onChange={(v) => setBannerModal({ ...bannerModal, title: v })} testid="banner-title-input" />
              <ImageUpload label="Εικόνα banner" value={bannerModal.image} onChange={(v) => setBannerModal({ ...bannerModal, image: v })} />
              <div>
                <label className="block text-sm font-semibold mb-1.5">Σύνδεση με επιχείρηση (κλικ → επιχείρηση)</label>
                <select value={bannerModal.business_id || ""} onChange={(e) => setBannerModal({ ...bannerModal, business_id: e.target.value })} data-testid="banner-business-select" className="w-full h-10 px-3 rounded-xl bg-muted outline-none focus:ring-2 ring-primary">
                  <option value="">— Καμία —</option>
                  {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={bannerModal.active} onCheckedChange={(v) => setBannerModal({ ...bannerModal, active: v })} /> Ενεργό</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setBannerModal(null)}>Άκυρο</Button>
            <Button className="rounded-full" onClick={saveBanner} disabled={saving} data-testid="banner-save-button">Αποθήκευση</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promotion modal */}
      <Dialog open={!!promoModal} onOpenChange={(o) => !o && setPromoModal(null)}>
        <DialogContent data-testid="promo-modal">
          <DialogHeader><DialogTitle className="font-display">{promoModal?.id ? "Επεξεργασία προσφοράς" : "Νέα προσφορά"}</DialogTitle></DialogHeader>
          {promoModal && (
            <div className="space-y-4">
              <Field label="Τίτλος" value={promoModal.title} onChange={(v) => setPromoModal({ ...promoModal, title: v })} testid="promo-title-input" />
              <div>
                <label className="block text-sm font-semibold mb-1.5">Περιγραφή</label>
                <textarea rows={3} value={promoModal.description} onChange={(e) => setPromoModal({ ...promoModal, description: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-muted outline-none resize-none" />
              </div>
              <ImageUpload label="Εικόνα" value={promoModal.image} onChange={(v) => setPromoModal({ ...promoModal, image: v })} />
              <div>
                <label className="block text-sm font-semibold mb-1.5">Σύνδεση με επιχείρηση (κλικ → επιχείρηση)</label>
                <select value={promoModal.business_id || ""} onChange={(e) => setPromoModal({ ...promoModal, business_id: e.target.value })} data-testid="promo-business-select" className="w-full h-10 px-3 rounded-xl bg-muted outline-none focus:ring-2 ring-primary">
                  <option value="">— Καμία —</option>
                  {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={promoModal.active} onCheckedChange={(v) => setPromoModal({ ...promoModal, active: v })} /> Ενεργή</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPromoModal(null)}>Άκυρο</Button>
            <Button className="rounded-full" onClick={savePromo} disabled={saving} data-testid="promo-save-button">Αποθήκευση</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-sm font-semibold mb-1.5">{label}</label>
    <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
           className="w-full h-10 px-3 rounded-xl bg-muted outline-none focus:ring-2 ring-primary" />
  </div>
);

export default AdminDashboard;
